import "bootstrap/dist/css/bootstrap.min.css";
import * as d3 from "d3";
import React, { useEffect, useRef } from "react";

import { COLORS } from "utils/colors";

var origin;
var target;
var svg;

/**
 * SkyPlot component renders a polar sky plot using D3 that visualizes
 * current and target azimuth and elevation positions as circles.
 * @memberof module:components/graphs
 * @param {Object} props
 * @param {number} props.width - Width of the SVG container
 * @param {number} props.height - Height of the SVG container
 * @param {number} props.azCurrent - Current azimuth angle in degrees
 * @param {number} props.elCurrent - Current elevation angle in degrees
 * @param {number} props.azTarget - Target azimuth angle in degrees
 * @param {number} props.elTarget - Target elevation angle in degrees
 * @param {boolean} props.disabled - Whether the plot is disabled (opacity changes)
 *
 * @returns {JSX.Element} Rendered SVG sky plot
 */
const RealtimePolarGraph = ({
  width,
  height,
  azCurrent,
  elCurrent,
  azTarget,
  elTarget,
  disabled,
  defaultOptiopns,
}) => {
  const ref = useRef();
  const targetAngleRef = useRef({});
  var margin = 70;
  var radius = Math.min(width, height) / 2 - margin;

  /**
   * Effect to clear and redraw the SVG plot once on component mount.
   */
  useEffect(() => {
    if (d3.select(ref.current)) d3.select(ref.current).selectAll("*").remove();

    draw(true);
  }, []);

  /**
   * Effect to update SVG opacity based on `disabled` prop changes.
   */
  useEffect(() => {
    svg.style("opacity", disabled ? 0.5 : 1);
  }, [disabled]);

  /**
   * Effect to update the target circle position whenever azTarget or elTarget changes.
   */
  useEffect(() => {
    const targetsData = Array.isArray(azTarget)
      ? azTarget
          .map((item, index, arr) => {
            
            const minEl = 20;
            const maxEl = 60;
            const size = arr.length;

            const el = Array.isArray(elTarget)
              ? (elTarget[index] ?? 0)
              : size > 1
                ? minEl + (index * (maxEl - minEl)) / (size - 1)
                : minEl;

            return {
              az: item?.bearing,
              centralFreq: item?.centralFreq,
              el: Math.round(el),
              id: index,
            };
          })
          .filter(
            (d) =>
              d.az !== undefined && d.az !== null && d.az >= 0 && d.az <= 360,
          )
      : [];

    // no targets

    
    if (targetsData.length === 0) {
      //target.selectAll(".target-group").style("opacity", 0.3);
      return;
    }

    // JOIN
    const groups = target
      .selectAll(".target-group")
      .data(targetsData, (d) => d.id);

    // EXIT
    groups.exit().remove();

    // ENTER
    const enter = groups
      .enter()
      .append("g")
      .attr("class", "target-group")
      .style("opacity", 0);

    // circle
    enter.append("circle").attr("fill", "red").attr("r", 15);

    // text
    enter
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", 5)
      .attr("font-size", "10px")
      .attr("font-weight", "bold")
      .attr("fill", "white");

    // MERGE
    const merged = enter.merge(groups);

    merged.each(function (d) {
      const group = d3.select(this);

      const targetRadius = radius * Math.sin(((90 - d.el) * Math.PI) / 180);

      const previousAz = targetAngleRef.current?.[d.id] ?? d.az;

      const interpolateAz = d3.interpolateNumber(previousAz, d.az);

      group
        .style("opacity", 1)
        .transition()
        .duration(1000)
        .ease(d3.easeCubicOut)
        .attrTween("transform", () => (t) => {
          const interpolatedAz = interpolateAz(t);

          const x =
            targetRadius *
            Math.cos(((interpolatedAz - 90) * Math.PI * 2) / 360);

          const y =
            targetRadius *
            Math.sin(((interpolatedAz - 90) * Math.PI * 2) / 360);

          return `translate(${x}, ${y})`;
        });

      // label
      group
        .select("text")
        .text(d.centralFreq != null ? `${d.centralFreq}` : `T${d.id + 1}`);
    });

    // store previous azimuths
    targetAngleRef.current = targetsData.reduce((acc, d) => {
      acc[d.id] = d.az;
      return acc;
    }, {});
  }, [azTarget, elTarget]);

  /**
   * Effect to update the origin circle position whenever azCurrent or elCurrent changes.
   */
  useEffect(() => {
    origin
      .attr(
        "cx",
        radius *
          Math.sin(((90 - elCurrent) * Math.PI) / 180) *
          Math.cos((((azCurrent - 90) % 360) * (Math.PI * 2)) / 360),
      )
      .attr(
        "cy",
        radius *
          Math.sin(((90 - elCurrent) * Math.PI) / 180) *
          Math.sin((((azCurrent - 90) % 360) * (Math.PI * 2)) / 360),
      );
  }, [azCurrent, elCurrent]);

  /**
   * Draws the sky plot with polar coordinate circles and direction labels.
   *
   */
  const draw = () => {
    // Create dummy data

    var r = d3.scaleLinear().domain([90, 0]).range([0, radius]);

    svg = d3
      .select(ref.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")");

    svg = d3
      .select(ref.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")");

    svg
      .append("text")
      .attr("y", -height / 2 + 15)
      .attr("font-size", "15px")
      .attr("text-anchor", "middle")
      .attr("stroke", "white")
      .attr("letter-spacing", "0.054em")
      .text(defaultOptiopns.title);

    origin = svg
      .append("circle")
      .attr("fill", "green")
      .attr("r", 10)

      .attr(
        "cx",
        radius *
          Math.sin(((90 - elCurrent) * Math.PI) / 180) *
          Math.cos((((azCurrent - 90) % 360) * (Math.PI * 2)) / 360),
      )
      .attr(
        "cy",
        radius *
          Math.sin(((90 - elCurrent) * Math.PI) / 180) *
          Math.sin((((azCurrent - 90) % 360) * (Math.PI * 2)) / 360),
      );

    target = svg.append("g").attr("class", "targets");

    var gr = svg
      .append("g")
      .attr("class", "r axis")
      .selectAll("g")
      .data(r.ticks(5))
      .enter()
      .append("g");

    gr.append("circle").attr("r", r);

    var ga = svg
      .append("g")
      .attr("class", "a axis")
      .selectAll("g")
      .data(d3.range(0, 360, 45))
      .enter()
      .append("g")
      .attr("fill", "white")
      .attr("transform", function (d) {
        return "rotate(" + (d - 90) + ")";
      });

    ga.append("line").attr("x2", radius);

    const directionMap = {
      0: "N",
      90: "E",
      180: "S",
      270: "W",
    };

    ga.append("text")
      .attr("x", radius + 12)
      .attr("text-anchor", "middle")
      .attr("color", "white")
      .attr("transform", (d) => {
        if (d === 180) return `rotate(-90 ${radius + 12},0) translate(0,10)`;

        return `rotate(90 ${radius + 12},0)`;
      })
      .text((d) => (directionMap[d] ? `${directionMap[d]} (${d}°)` : ""));

    svg.append("g").attr("transform", "rotate(-90)");
  };
  return (
    <>
      <div
        className="bar-graph p-2"
        id="container"
        style={{ justifyContent: "center" }}
      >
        <svg
          ref={ref}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{
            width: "100%",
            // height: "auto",
            display: "block",
          }}
        ></svg>
      </div>
    </>
  );
};

export default RealtimePolarGraph;
